import { useCallback, useMemo, useState } from 'react'
import { ObservableQueryFields } from '@apollo/client'
import { IPageInfo } from 'types/graphqlTypes'

export interface IUseGraphQLPaginationProps<TNode, TData, TVariables> {
  fetchMore?: ObservableQueryFields<TData, TVariables>['fetchMore']
  pageInfo?: IPageInfo
  startPage?: number
  perPage: number
  nodes: TNode[]
  totalCount: number
  variables: ObservableQueryFields<TData, TVariables>['variables']
}

type THandlePageChange = (pageNumber: number) => Promise<void>

type THandleResetPagination = () => void

interface IUseGraphQLPaginationResult<TNode> {
  currentPage: number
  handlePageChange: THandlePageChange
  handleResetPagination: THandleResetPagination
  hasNextPage: boolean
  paginatedNodes: TNode[]
  totalPages: number
}

/**
 * @definition Paginates the nodes of a GraphQL Connection result
 * @note To update loading state on refetch/fetchMore, set `notifyOnNetworkStatusChange: true` on the query options.
 */
export const useGraphQLPagination = <TNode, TData, TVariables>({
  fetchMore,
  nodes,
  pageInfo,
  perPage,
  startPage = 1,
  totalCount,
  variables,
}: IUseGraphQLPaginationProps<TNode, TData, TVariables>): IUseGraphQLPaginationResult<TNode> => {
  /**
   * @description 1-based, per DPL component.
   * @param startPage for integration with query params.
   * @default 1
   */
  const [currentPage, setCurrentPage] = useState<number>(startPage)

  /** See hasNextPage comment below */
  const { endCursor, hasNextPage: hasMorePagesToFetch = false } = pageInfo ?? {}

  /**
   * Indicates number of pages already fetched, rounded up to nearest integer.
   */
  const numPagesFetched = Math.ceil(nodes.length / perPage)
  const totalPages = useMemo(
    () => (typeof totalCount === 'number' ? Math.ceil(totalCount / perPage) : undefined),
    [perPage, totalCount]
  )

  /**
   * Has more pages to fetch, or has more already-fetched pages
   * Because Relay-style pagination is unidirectional, this value is assembled
   * from the already loaded (cached) nodes and the pageInfo value from GraphQL.
   */
  const hasNextPage = useMemo(() => {
    const hasMoreLoadedResults = nodes.length !== perPage && currentPage < numPagesFetched
    return hasMorePagesToFetch || hasMoreLoadedResults
  }, [currentPage, hasMorePagesToFetch, nodes.length, numPagesFetched, perPage])

  /**
   * Resulting nodes for the current page
   */
  const paginatedNodes = useMemo(() => {
    const startIndex = perPage * (currentPage - 1)
    return nodes.slice(startIndex, startIndex + perPage)
  }, [currentPage, nodes, perPage])

  const handlePageChange: THandlePageChange = useCallback(
    async pageNumber => {
      if (pageNumber > numPagesFetched && hasMorePagesToFetch && fetchMore) {
        await fetchMore({
          variables: {
            ...variables,
            after: endCursor,
            first: perPage,
          },
        })
      }
      setCurrentPage(pageNumber)
    },
    [endCursor, fetchMore, hasMorePagesToFetch, numPagesFetched, perPage, variables]
  )

  const handleResetPagination = useCallback(() => {
    setCurrentPage(1)
  }, [])

  return <IUseGraphQLPaginationResult<TNode>>{
    currentPage,
    handlePageChange,
    handleResetPagination,
    hasNextPage,
    paginatedNodes,
    totalPages,
  }
}
